import { PROPERTY_GRID_SUBJECT_PROPERTY } from '@aloft/toolkit-shared';
import { Typography } from '@aloft/toolkit-sui';

import React from 'react';
import { useFormContext } from 'react-hook-form';

import { selectOrder } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { AdjustmentField } from '../fields/AdjustmentField.tsx';
import { AgeWithYearBuiltField } from '../fields/AgeWithYearBuiltField.tsx';
import { DataSourceValueField } from '../fields/DataSourceValueField.tsx';
import { EditableEnumField } from '../fields/EditableEnumField.tsx';
import { EditableField } from '../fields/EditableField.tsx';
import { LabelField } from '../fields/LabelField.tsx';
import { ReadOnlyField } from '../fields/ReadOnlyField.tsx';
import { readOnlyFieldTypographyProps } from '../fields/consts.ts';
import { PropertyGridFieldProps } from '../fields/types.ts';
import { PropertyGridField, PropertyGridForm, PropertyGridSectionProps } from '../types.ts';

type PropertyGridRowProps<T, U extends string | number> = Pick<
  PropertyGridSectionProps,
  'columnIndex' | 'fieldTypes' | 'hasSeparateArvValue'
> & {
  field: PropertyGridField<T, U>;
  sectionIsNone?: boolean;
};

export const PropertyGridRow = <T, U extends string | number>({
  columnIndex,
  fieldTypes,
  field,
  sectionIsNone = false,
  hasSeparateArvValue = false,
}: PropertyGridRowProps<T, U>) => {
  const { hasArvOption } = useAppSelector(selectOrder);

  const { fieldName } = field;

  const { watch } = useFormContext<PropertyGridForm>();

  const columnType = watch(`columns.${columnIndex}.type`);
  const propertyId = watch(`columns.${columnIndex}.propertyId`);
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  // TODO - AV-169
  const showAivComps = true;
  const showArvComps = true;

  return (
    <>
      {fieldTypes.map((fieldType) => {
        let FieldComponent: React.ComponentType<PropertyGridFieldProps<T, U>> | undefined;
        let isArv = false;
        let colSpan = 1;

        // Special case for age fields - use custom component with year built
        const isAgeField = fieldName === 'editableData.improvements.age';

        switch (fieldType) {
          case 'dataSourceValue':
            FieldComponent = isAgeField ? AgeWithYearBuiltField : DataSourceValueField;
            break;
          // @ts-expect-error - Fallthrough intended
          case 'editableArvValue':
            if (columnType !== 'property' || !showArvComps || !isSubject || !hasSeparateArvValue) {
              break;
            }

            isArv = true;
          // eslint-disable-next-line no-fallthrough
          case 'editableValue':
            if (isSubject && !showAivComps && !isArv) break;
            if (isSubject && !isArv && hasArvOption && showArvComps && !hasSeparateArvValue) {
              colSpan = 2;
            }

            if (sectionIsNone) {
              return (
                <Typography
                  key={`${columnIndex}-${fieldName}-readOnlyValue`}
                  {...readOnlyFieldTypographyProps}
                >
                  None
                </Typography>
              );
            }

            if (isAgeField) FieldComponent = AgeWithYearBuiltField;
            else if (field.options) FieldComponent = EditableEnumField;
            else FieldComponent = EditableField;
            break;
          case 'readOnlyValue':
            FieldComponent = isAgeField ? AgeWithYearBuiltField : ReadOnlyField;
            break;
          case 'adjustment':
            FieldComponent = AdjustmentField;
            break;
          case 'label':
          default:
            FieldComponent = LabelField;
            break;
        }

        if (!FieldComponent) return null;

        return (
          <FieldComponent
            key={`${columnIndex}-${fieldName}-${fieldType}-field`}
            columnIndex={columnIndex}
            field={field}
            isArv={isArv}
            colSpan={colSpan}
          />
        );
      })}
    </>
  );
};
